
import React, { createContext, useContext, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { useAuthOperations } from '@/hooks/useAuthOperations';
import { useSessionInitialization } from '@/hooks/useSessionInitialization';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signUp: (email: string, password: string, userData?: any) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signInWithIdToken: (params: { provider: string; token: string; nonce?: string }) => Promise<{ data?: any; error: any }>;
  signOut: () => Promise<void>;
  enableMFA: () => Promise<{ data?: any; error: any }>;
  verifyMFA: (factorId: string, challengeId: string, code: string) => Promise<{ data?: any; error: any }>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const { signUp, signIn, signOut, enableMFA, verifyMFA, signInWithGoogle, signInWithIdToken } = useAuthOperations();

  // Initialize session and set up auth state listener with secure cookie handling
  useSessionInitialization({
    setUser,
    setSession,
    setLoading
  });

  const value = {
    user,
    session,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithIdToken,
    signOut,
    enableMFA,
    verifyMFA,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
